import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../../lib/firebase';
import { usePath, navigate, parseRoute } from '../../lib/router';
import Modal from '../shared/Modal';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { workspaceHeader, getActiveWorkspaceId } from '../../lib/workspaces';
import { useWorkspaces } from '../shared/WorkspaceProvider';
import { User, Building2, Search, RefreshCw, CheckCircle, XCircle, Eye, X, Users, Briefcase, Award, Plane, Check, AlertCircle, Mail, Phone, MapPin, Ban, Clock, Shield, ArrowUp, ArrowDown, Trash2, Plus, Send, Archive, ArchiveRestore, Crown, UserCheck } from 'lucide-react';
import { AdminOrgInviteModal } from './AdminOrgInviteModal';
import OrgDocumentsSection from './OrgDocumentsSection';
import OrgSpecialRequestsSection from './OrgSpecialRequestsSection';
import { rateInputValue, applyRateInput, withoutProgramRate, pruneRatesToAssignedPrograms } from '../../lib/referrerRateEditor';
import { ListPagination, useListPagination } from './ListPagination';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  active: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  inactive: 'bg-slate-100 text-slate-600',
  suspended: 'bg-red-100 text-red-700',
  deactivated: 'bg-red-100 text-red-700',
};

// Attach the active workspace header so these admin requests are scoped to the
// workspace the admin is actually working in — otherwise raw fetches fall back
// to the host-resolved (default) workspace and 403 for admins whose own
// workspace differs.
const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};

interface ReferrersManagerProps {
  canEdit?: boolean;
}

export default function ReferrersManager({ canEdit = true }: ReferrersManagerProps = {}) {
  const [referrers, setReferrers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [programOptions, setProgramOptions] = useState<{ id: string; name: string }[]>([]);
  // Commission records for the open referrer (keyed by applicationId) —
  // powers the per-candidate amount/status and the commission history
  // shown in the detail's Referred Candidates section. Sourced from the
  // admin Finance commissions list; degrades silently without finance
  // permission (the stamped amount on each candidate still shows).
  const [detailCommissions, setDetailCommissions] = useState<Record<string, any>>({});

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync detail with URL path
  useEffect(() => {
    if (currentRoute.type === 'referrers_detail') {
      const targetId = currentRoute.id;
      if (detail?.id !== targetId) {
        const found = referrers.find(r => r.id === targetId);
        if (found) {
          if (detail?.id !== found.id) {
            setDetail(found);
          }
        } else if (referrers.length > 0 && !loading) {
          // If referrers are loaded but targetId is not found, clear detail to break loop
          setDetail(null);
        }
      }
    } else {
      if (detail && currentPath === '/referrers') {
        setDetail(null);
      }
    }
  }, [currentPath, currentRoute.type, referrers, loading]);

  const prevDetailRef = useRef(detail);

  // Sync URL path when detail changes
  useEffect(() => {
    if (detail?.id) {
      navigate(`/referrers/adminview/${detail.id}`);
    } else if (prevDetailRef.current && !detail && currentRoute.type === 'referrers_detail') {
      navigate('/referrers');
    }
    prevDetailRef.current = detail;
  }, [detail, currentRoute.type]);
  const [editingPrograms, setEditingPrograms] = useState<string[]>([]);
  const [editingProgramRates, setEditingProgramRates] = useState<Record<string, number>>({});
  const [savingPrograms, setSavingPrograms] = useState(false);
  const [originalEnhancedPermission, setOriginalEnhancedPermission] = useState<boolean | null>(null);
  const [linkModal, setLinkModal] = useState<{ id: string; name: string; email: string } | null>(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);

  // Hard delete (permanent organisation removal) — type-to-confirm state.
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Referrer Direct Creation State
  const [showCreateModal, setShowModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    type: 'individual',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    country: '',
    website: '',
    relatedPrograms: [] as string[]
  });
  const [creating, setCreating] = useState(false);

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      type: 'individual',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      country: '',
      website: '',
      relatedPrograms: []
    });
  };

  const handleCreateReferrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      showToast('Referrer name is required', 'error');
      return;
    }
    setCreating(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/referrers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(createForm),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create referrer');
      }
      showToast('Referrer created successfully', 'success');
      setShowModal(false);
      resetCreateForm();
      fetchReferrers();
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  // Follow-up Item 12: gate the initial fetch on workspace resolution.
  // workspaceHeader() reads localStorage at call time; fetching before
  // WorkspaceProvider validated the stored id reads the WRONG workspace
  // context and the list sticks at stale zeros until a manual Refresh.
  const { loading: workspaceLoading, activeWorkspaceId } = useWorkspaces();
  const fetchReferrers = useCallback(async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch('/api/admin/referrers', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to fetch referrers');
      const data = await res.json();
      setReferrers(data.referrers || []);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { if (!workspaceLoading) fetchReferrers(); }, [fetchReferrers, workspaceLoading]);

  // Fetch available programs for the program picker
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await wsFetch('/api/admin/programs', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setProgramOptions(data.programs || []);
        }
      } catch { /* ok */ }
    };
    fetchPrograms();
  }, []);

  // Sync editingPrograms & editingProgramRates when detail modal opens
  useEffect(() => {
    if (detail) {
      setEditingPrograms(detail.relatedPrograms ? [...detail.relatedPrograms] : []);
      // Load the stored rates, dropping residue keys for programs that are
      // no longer assigned (left behind by earlier removals) so the editor
      // state — and the next save — carries rates only for assigned
      // programs. UNSET programs keep NO key: they render as "Not set",
      // never as a phantom 0.
      const loadedRates = detail.programRates ? { ...detail.programRates } : (detail.programCommissions ? { ...detail.programCommissions } : {});
      const assignedIds = [...(detail.relatedPrograms || []), ...(detail.assignedProgramIds || [])];
      setEditingProgramRates(assignedIds.length > 0 ? pruneRatesToAssignedPrograms(loadedRates, assignedIds) : loadedRates);
      // Track original enhancedPermission for dirty-check (set only on modal open, not on every detail change)
      setOriginalEnhancedPermission(detail.enhancedPermission === true);
    }
  }, [detail?.id]); // Only re-run when a DIFFERENT referrer modal opens, not when detail updates

  // Load this referrer's commission records (Finance tab data) so the
  // Referred Candidates section can show each candidate's current
  // commission amount/status and full event history (stamp, admin edits,
  // removals — all with attribution).
  useEffect(() => {
    if (!detail?.id) { setDetailCommissions({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await wsFetch('/api/admin/finance/commissions', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const map: Record<string, any> = {};
        for (const c of (data.commissions || [])) {
          if (c.referrerId === detail.id && c.applicationId) map[c.applicationId] = c;
        }
        setDetailCommissions(map);
      } catch { /* finance permission may not be held — degrade silently */ }
    })();
    return () => { cancelled = true; };
  }, [detail?.id]);

  const handleSaveReferrer = async () => {
    if (!detail) return;
    setSavingPrograms(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const originalStatus = referrers.find(r => r.id === detail.id)?.status || 'active';
      const newStatus = detail.status || 'active';
      // Suspension changes go through the unified org status endpoint so the
      // transition is validated, linked users lose/regain access, and the
      // change is audit-logged. (The raw PUT would write the status with no
      // propagation.)
      const suspensionChange =
        (newStatus === 'deactivated' && originalStatus !== 'deactivated') ||
        (originalStatus === 'deactivated' && newStatus !== 'deactivated');
      if (suspensionChange) {
        const targetStatus = newStatus === 'deactivated' ? 'deactivated' : 'approved';
        // The unified endpoint requires a reason for deactivation — without it
        // the request 400s and the suspension silently never happens.
        const sres = await wsFetch(`/api/admin/accounts/referrer/${detail.id}/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            status: targetStatus,
            ...(targetStatus === 'deactivated' ? { reason: 'Deactivated by admin from Referrers Manager.' } : {}),
          }),
        });
        if (!sres.ok) {
          const errData = await sres.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to update status');
        }
      }
      const res = await wsFetch(`/api/admin/referrers/${detail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: detail.name,
          contactName: detail.contactName,
          contactEmail: detail.contactEmail,
          contactPhone: detail.contactPhone,
          country: detail.country,
          website: detail.website,
          // Status is managed by the unified endpoint when suspension changed.
          ...(suspensionChange ? {} : { status: detail.status }),
          notes: detail.notes,
          relatedPrograms: editingPrograms,
          programRates: editingProgramRates,
          programCommissions: editingProgramRates,
          enhancedPermission: detail.enhancedPermission === true,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to update referrer');
      }
      const savedPermission = detail.enhancedPermission === true;
      setDetail((prev: any) => ({
        ...prev,
        relatedPrograms: [...editingPrograms],
        programRates: { ...editingProgramRates },
        programCommissions: { ...editingProgramRates },
        enhancedPermission: savedPermission
      }));
      setReferrers(prev => prev.map(r => (r.id === detail.id ? {
        ...r,
        name: detail.name,
        contactName: detail.contactName,
        contactEmail: detail.contactEmail,
        contactPhone: detail.contactPhone,
        country: detail.country,
        website: detail.website,
        status: detail.status,
        notes: detail.notes,
        relatedPrograms: [...editingPrograms],
        programRates: { ...editingProgramRates },
        programCommissions: { ...editingProgramRates },
        enhancedPermission: savedPermission
      } : r)));
      setOriginalEnhancedPermission(savedPermission);
      showToast('Referrer updated successfully', 'success');
    } catch (err: any) { showToast(err.message || 'Failed to update referrer', 'error'); }
    finally { setSavingPrograms(false); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      // Suspend / unsuspend through the unified org status endpoint so the
      // transition is validated, linked users lose/regain access, and the
      // change is audit-logged. (The old PUT /api/admin/referrers/:id wrote a
      // raw 'inactive' status nothing else recognized, with no propagation.)
      const targetStatus = status === 'active' ? 'approved' : 'deactivated';
      // The unified endpoint requires a reason for deactivation — without it
      // the request 400s and the suspension silently never happens.
      const res = await wsFetch(`/api/admin/accounts/referrer/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          status: targetStatus,
          ...(targetStatus === 'deactivated' ? { reason: 'Deactivated by admin from Referrers Manager.' } : {}),
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to update status');
      }
      const storedStatus = targetStatus === 'approved' ? 'active' : 'deactivated';
      setReferrers(prev => prev.map(r => (r.id === id ? { ...r, status: storedStatus } : r)));
      showToast(`Referrer ${targetStatus === 'approved' ? 'activated' : 'deactivated'}`, 'success');
    } catch (err: any) {
      const msg = err.message || 'Failed to update status';
      showToast(msg, 'error');
      // Gate 1: activation blocked because commission amounts are unset —
      // open the referrer's record so the admin can enter them under
      // Programs & Commission Rates, then activate again.
      if (/commission amount/i.test(msg)) {
        const r = referrers.find(x => x.id === id);
        if (r) setDetail(r);
      }
    }
  };

  // Archive / unarchive: archived orgs are hidden from the main list but stay
  // fully accessible via the Archived view. Approved AND rejected referrers
  // remain visible until explicitly archived.
  const handleArchiveToggle = async (id: string, archive: boolean, name?: string) => {
    const label = name || 'this referrer';
    if (!(await confirmDialog(archive
      ? `Archive ${label}? It will be hidden from the active referrer list. You can restore it from the archive at any time.`
      : `Restore ${label} from the archive? It will return to the active referrer list.`))) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/referrers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          archived: archive,
          archivedAt: archive ? new Date().toISOString() : null,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to update archive status');
      }
      setReferrers(prev => prev.map(r => (r.id === id ? { ...r, archived: archive, archivedAt: archive ? new Date().toISOString() : null } : r)));
      showToast(archive ? 'Referrer archived' : 'Referrer restored from archive', 'success');
    } catch (err: any) { showToast(err.message || 'Failed to update archive status', 'error'); }
  };

  // HARD DELETE: permanently removes the organisation from the workspace.
  // Referred applications and commission history are preserved server-side
  // and render with an "organisation removed" marker. Irreversible — the
  // confirm modal requires typing the organisation's name.
  const handleDeleteReferrer = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await wsFetch(`/api/admin/referrers/${deleteTarget.id}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete referrer');
      setReferrers(prev => prev.filter(r => r.id !== deleteTarget.id));
      setDetail(null);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      showToast(`Referrer organisation deleted — ${data.preservedApplications ?? 0} referred application(s) preserved`, 'success');
      if (data.codeSharedWithOtherOrg) {
        notifyToast('warning', 'Note: another referrer record shares this referral code, so the code itself remains active.');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to delete referrer', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = referrers.filter(r => {
    // Archived orgs are hidden from the main list; the Archived view shows only them.
    if (!!r.archived !== showArchived) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const idStr = (r.referrerId || ('REF-' + (r.numericId || ''))).toLowerCase();
    return idStr.includes(q) ||
           (r.name || '').toLowerCase().includes(q) ||
           (r.contactName || '').toLowerCase().includes(q) ||
           (r.contactEmail || r.email || '').toLowerCase().includes(q);
  });

  // Client-side paging over the full filtered set (the endpoint returns
  // every referrer; see ListPagination for the shared pattern). Resets to
  // page 1 when the search text or Active/Archived view changes.
  const pager = useListPagination(filtered, `${search}|${showArchived}`);

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Toasts render via the global root-level notification layer. */}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Referrer Management</h1>
          <p className="text-xs text-slate-500">{referrers.length} referrer{referrers.length !== 1 ? 's' : ''} registered</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, contact, or email..." className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-white" />
          </div>
          <div className="flex items-center rounded-xl border border-slate-200 overflow-hidden text-xs font-bold">
            <button onClick={() => setShowArchived(false)} className={`px-3 py-2 transition cursor-pointer ${!showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>Active</button>
            <button onClick={() => setShowArchived(true)} className={`px-3 py-2 transition cursor-pointer flex items-center gap-1 ${showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}><Archive className="h-3.5 w-3.5" />Archived ({referrers.filter(r => r.archived).length})</button>
          </div>
          {canEdit && (
            <>
              <button onClick={() => { resetCreateForm(); setShowModal(true); }} className="flex items-center gap-1.5 px-4 py-2 btn-primary text-white rounded-xl text-xs font-bold cursor-pointer">
                <Plus className="h-3.5 w-3.5" /> Add Referrer
              </button>
              <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-1.5 px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-xs font-bold cursor-pointer">
                <Send className="h-3.5 w-3.5" /> Invite Referrer
              </button>
            </>
          )}
          <button onClick={fetchReferrers} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer" title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Referrers', value: referrers.length, icon: User, color: 'text-brand' },
          { label: 'Active', value: referrers.filter(r => r.status === 'active' || r.status === 'approved').length, icon: CheckCircle, color: 'text-emerald-600' },
          { label: 'Pending', value: referrers.filter(r => r.status === 'pending').length, icon: Clock, color: 'text-amber-600' },
          { label: 'Total Candidates Referred', value: referrers.reduce((s, r) => s + (r.stats?.totalReferred || 0), 0), icon: Users, color: 'text-purple-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
            <div className="p-2 rounded-lg w-fit bg-slate-50 mb-2"><s.icon className={`h-4 w-4 ${s.color}`} /></div>
            <p className="text-lg font-bold text-slate-900">{s.value}</p>
            <p className="text-[11px] text-slate-500 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12"><div className="premium-loader mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center">
          <User className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No referrers found</p>
          <p className="text-xs text-slate-500 mt-1">Referrers will appear here once they register and complete onboarding.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
              <tr>
                <th className="px-4 py-3 text-left">Name / Type</th>
                <th className="px-4 py-3 text-left">Contact</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-center">Referred</th>
                <th className="px-4 py-3 text-center">Hired</th>
                <th className="px-4 py-3 text-center">Visa Approved</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pager.pageItems.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg ${r.type === 'company' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                        {r.type === 'company' ? <Building2 className="h-4 w-4 text-purple-600" /> : <User className="h-4 w-4 text-brand" />}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{r.name || ''}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-mono text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded font-bold">
                            {r.inviteCode || '—'}
                          </span>
                          <span className="text-[11px] text-slate-500">{r.type === 'company' ? 'Company' : 'Individual'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-slate-700">{r.contactName || r.name || ''}</p>
                    <p className="text-[11px] text-slate-500">{r.contactEmail || r.email || ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>
                      {r.status || ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-900">{r.stats?.totalReferred || 0}</td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-900">{r.stats?.hired || 0}</td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-900">{r.stats?.visaApproved || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setDetail(r)} className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition cursor-pointer" title="View Details"><Eye className="h-4 w-4" /></button>
                      {r.status === 'active' || r.status === 'approved' ? (
                        <button onClick={() => handleStatusChange(r.id, 'inactive')} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition cursor-pointer" title="Deactivate"><Ban className="h-4 w-4" /></button>
                      ) : (
                        <button onClick={() => handleStatusChange(r.id, 'active')} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition cursor-pointer" title="Activate"><CheckCircle className="h-4 w-4" /></button>
                      )}
                      {r.archived ? (
                        <button onClick={() => handleArchiveToggle(r.id, false, r.contactName || r.name)} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition cursor-pointer" title="Restore from archive"><ArchiveRestore className="h-4 w-4" /></button>
                      ) : (
                        <button onClick={() => handleArchiveToggle(r.id, true, r.contactName || r.name)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition cursor-pointer" title="Archive"><Archive className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ListPagination
          page={pager.page}
          pageCount={pager.pageCount}
          total={pager.total}
          start={pager.start}
          end={pager.end}
          pageSize={pager.pageSize}
          onPageChange={pager.setPage}
          onPageSizeChange={pager.setPageSize}
          itemLabel="referrers"
        />
      )}

      {/* Detail Modal */}
      {detail && (
        <Modal title={detail.name}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${detail.type === 'company' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                  {detail.type === 'company' ? <Building2 className="h-5 w-5 text-purple-600" /> : <User className="h-5 w-5 text-brand" />}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{detail.name}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[detail.status] || 'bg-slate-100 text-slate-600'}`}>{detail.status || ''}</span>
                    {/* §5.2: the SAME derived REF invite code the list and
                        the referrer's own portal show — never the doc id. */}
                    <span className="text-[10px] font-semibold text-slate-500">Invite Code: <span className="font-mono font-bold text-slate-800">{detail.inviteCode || '—'}</span></span>
                  </div>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-5 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'Total Referred', value: detail.stats?.totalReferred || 0, icon: Users },
                  { label: 'Submitted', value: detail.stats?.submitted || 0, icon: CheckCircle },
                  { label: 'Active', value: detail.stats?.active || 0, icon: Award },
                  { label: 'Hired', value: detail.stats?.hired || 0, icon: Briefcase },
                  { label: 'Visa Approved', value: detail.stats?.visaApproved || 0, icon: Plane },
                ].map((s, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
                    <s.icon className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                    <p className="text-lg font-bold text-slate-900">{s.value}</p>
                    <p className="text-[11px] text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Organization documents (Slice 1) */}
              <OrgDocumentsSection orgType="referrer" orgId={detail.id} canEdit={canEdit} />

              {/* Organization special requests (Slice 2) */}
              <OrgSpecialRequestsSection orgType="referrer" orgId={detail.id} canEdit={canEdit} />

              {/* Editable Profile & Contact Info */}
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Edit Referrer Profile & Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Agency / Partner Name *</label>
                    <input
                      type="text"
                      value={detail.name || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={detail.contactName || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, contactName: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Contact Email</label>
                    <input
                      type="email"
                      value={detail.contactEmail || detail.email || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, contactEmail: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Contact Phone</label>
                    <input
                      type="text"
                      value={detail.contactPhone || detail.phone || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, contactPhone: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Country</label>
                    <input
                      type="text"
                      value={detail.country || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, country: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Website</label>
                    <input
                      type="text"
                      value={detail.website || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, website: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Status</label>
                    <select
                      value={detail.status || 'active'}
                      onChange={e => setDetail((prev: any) => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white font-bold text-slate-800"
                    >
                      <option value="active">Active (Approved)</option>
                      <option value="pending">Pending Approval</option>
                      <option value="deactivated">Suspended</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-600 mb-1">Internal Admin Notes</label>
                    <textarea
                      rows={2}
                      value={detail.notes || ''}
                      onChange={e => setDetail((prev: any) => ({ ...prev, notes: e.target.value }))}
                      placeholder="Internal staff notes for this referrer..."
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Enhanced Permission toggle */}
              <div className="flex items-center justify-between p-3 border border-slate-200 rounded-xl bg-white">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <div>
                    <p className="text-xs font-bold text-slate-800">Enhanced Permission</p>
                    <p className="text-[11px] text-slate-500">Grants admin-like applicant view with restrictions</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDetail((prev: any) => ({ ...prev, enhancedPermission: !prev.enhancedPermission }));
                    // Also sync editingPrograms when toggling so the data reflects changes immediately
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${detail.enhancedPermission ? 'bg-purple-600' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${detail.enhancedPermission ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Linked Users */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-indigo-600" />
                    Linked Users ({(detail.linkedUsers || []).length})
                  </h3>
                  <button
                    onClick={() => {
                      setLinkModal({ id: detail.id, name: detail.name, email: detail.contactEmail || detail.email || '' });
                      setLinkEmail('');
                    }}
                    className="px-2.5 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add User
                  </button>
                </div>
                {(!detail.linkedUsers || detail.linkedUsers.length === 0) ? (
                  <p className="text-xs text-slate-500 text-center py-3">No users linked to this referrer</p>
                ) : (
                  <div className="space-y-2">
                    {detail.linkedUsers.filter((u: any) => u.status !== 'removed').map((u: any) => (
                      <div key={u.uid} className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <User className="h-3.5 w-3.5 text-indigo-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{u.email}</p>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${
                                u.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                u.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
                              }`}>{u.status}</span>
                              <span className={`px-1.5 py-0.5 rounded font-medium ${u.agentRole === 'super' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {u.agentRole === 'super' ? 'Super Agent' : 'Regular Agent'}
                              </span>
                              {detail.userId && u.uid === detail.userId && (
                                <span className="px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700" title="Designated Super Agent — protected from removal/demotion; use Make Super Agent on another member to change">Owner</span>
                              )}
                              {u.invitedAt && <span>Since {new Date(u.invitedAt).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {u.status === 'active' && u.agentRole === 'regular' && (
                            <button onClick={async () => {
                              try {
                                const token = await auth.currentUser?.getIdToken();
                                await wsFetch(`/api/admin/referrers/${detail.id}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ uid: u.uid, action: 'promote-agent' }),
                                });
                                showToast('Promoted to Super Agent', 'success');
                                setDetail((prev: any) => ({
                                  ...prev,
                                  linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, agentRole: 'super' } : lu)
                                }));
                              } catch { showToast('Failed to promote', 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Promote to Super Agent">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.agentRole === 'super' && u.uid !== detail.userId && detail.linkedUsers.filter((lu: any) => lu.agentRole === 'super' && lu.status !== 'removed').length > 1 && (
                            <button onClick={async () => {
                              try {
                                const token = await auth.currentUser?.getIdToken();
                                await wsFetch(`/api/admin/referrers/${detail.id}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ uid: u.uid, action: 'demote-agent' }),
                                });
                                showToast('Demoted to Regular Agent', 'success');
                                setDetail((prev: any) => ({
                                  ...prev,
                                  linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, agentRole: 'regular' } : lu)
                                }));
                              } catch { showToast('Failed to demote', 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer" title="Demote to Regular Agent">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== detail.userId && (
                            <button onClick={async () => {
                              if (!(await confirmDialog(`Make ${u.email} the Super Agent for this referrer?\n\nThe current Super Agent becomes a regular agent and loses Super Agent protection (they can then be removed, demoted, or deactivated normally). ${u.email} gains that protection. This is the only way the Super Agent designation changes.`))) return;
                              try {
                                const token = await auth.currentUser?.getIdToken();
                                await wsFetch(`/api/admin/referrers/${detail.id}/change-super`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ uid: u.uid }),
                                });
                                showToast('Super Agent changed', 'success');
                                setDetail((prev: any) => ({
                                  ...prev,
                                  userId: u.uid,
                                  linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, agentRole: 'super' } : lu.uid === prev.userId ? { ...lu, agentRole: 'regular' } : lu)
                                }));
                              } catch { showToast('Failed to change Super Agent', 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition cursor-pointer" title="Make Super Agent (formal change)">
                              <Crown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== detail.userId && (
                            <button onClick={async () => {
                              try {
                                const token = await auth.currentUser?.getIdToken();
                                await wsFetch(`/api/admin/referrers/${detail.id}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ uid: u.uid, action: 'block' }),
                                });
                                showToast('User deactivated', 'success');
                                setDetail((prev: any) => ({
                                  ...prev,
                                  linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, status: 'blocked', deactivatedBy: 'admin' } : lu)
                                }));
                              } catch { showToast('Failed to deactivate', 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Deactivate (admin) — the org super cannot reactivate over this">
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'blocked' && (
                            <button onClick={async () => {
                              try {
                                const token = await auth.currentUser?.getIdToken();
                                await wsFetch(`/api/admin/referrers/${detail.id}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ uid: u.uid, action: 'unblock' }),
                                });
                                showToast('User reactivated', 'success');
                                setDetail((prev: any) => ({
                                  ...prev,
                                  linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, status: 'active', deactivatedBy: undefined } : lu)
                                }));
                              } catch { showToast('Failed to reactivate', 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition cursor-pointer" title="Reactivate">
                              <UserCheck className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.uid !== detail.userId && (
                          <button onClick={async () => {
                            if (!(await confirmDialog(`Remove ${u.email} from this referrer?`))) return;
                            try {
                              const token = await auth.currentUser?.getIdToken();
                              await wsFetch(`/api/admin/referrers/${detail.id}/user-action`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                body: JSON.stringify({ uid: u.uid, action: 'remove' }),
                              });
                              showToast('User removed from referrer', 'success');
                              setDetail((prev: any) => ({
                                ...prev,
                                linkedUsers: prev.linkedUsers.map((lu: any) => lu.uid === u.uid ? { ...lu, status: 'removed' } : lu)
                              }));
                            } catch { showToast('Failed to remove', 'error'); }
                          }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition cursor-pointer" title="Remove from referrer">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Programs — editable with custom program commission rates */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Programs & Commission Rates</h3>
                </div>
                <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-xl bg-white">
                  {programOptions.length === 0 && (
                    <p className="text-[11px] text-slate-500 py-1">Loading programs...</p>
                  )}
                  {programOptions.map((prog) => {
                    const pName = prog.name || prog.id;
                    const selected = editingPrograms.includes(prog.id);
                    return (
                      <button
                        key={prog.id}
                        type="button"
                        onClick={() => {
                          if (selected) {
                            // Removing a program also drops its rate key:
                            // a removed program must leave no rate residue,
                            // and re-adding it later starts UNSET.
                            setEditingProgramRates(prev => withoutProgramRate(prev, prog.id));
                          }
                          setEditingPrograms(prev =>
                            selected
                              ? prev.filter((id: string) => id !== prog.id)
                              : [...prev, prog.id]
                          );
                        }}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition cursor-pointer ${
                          selected
                            ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                            : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {pName}
                      </button>
                    );
                  })}
                </div>

                {/* Per-Program Commission Rates */}
                {editingPrograms.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[11px] font-bold text-slate-700">Commission Rate Per Assigned Program ($):</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {editingPrograms.map((pId) => {
                        const progObj = programOptions.find(p => p.id === pId);
                        const pName = progObj?.name || pId;
                        // UNSET (no key in the map) renders EMPTY with a
                        // "Not set" placeholder — never as 0. An explicitly
                        // set 0 renders as 0. There is no default rate.
                        const inputVal = rateInputValue(editingProgramRates, pId);
                        const isSet = inputVal !== '';
                        return (
                          <div key={pId} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                            <span className="font-semibold text-slate-800 truncate max-w-[160px]">{pName}</span>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              {!isSet && <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">Not set</span>}
                              <span className="text-slate-400 font-bold">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={inputVal}
                                onChange={(e) => {
                                  // Clearing the input removes the key
                                  // (persists UNSET) — it never writes 0.
                                  // Typing 0 sets an explicit, valid 0.
                                  setEditingProgramRates(prev => applyRateInput(prev, pId, e.target.value));
                                }}
                                className="w-24 px-2 py-1 bg-white border border-slate-200 rounded-lg text-right font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                placeholder="Not set"
                                aria-label={`${pName} commission rate ($)`}
                                title={`${pName} commission rate ($)`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(
                  JSON.stringify(detail.relatedPrograms || []) !== JSON.stringify(editingPrograms) ||
                  JSON.stringify(detail.programRates || {}) !== JSON.stringify(editingProgramRates) ||
                  originalEnhancedPermission !== detail.enhancedPermission
                ) && (
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={handleSaveReferrer}
                      disabled={savingPrograms}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-md shadow-purple-600/20"
                    >
                      {savingPrograms ? 'Saving...' : 'Save Program Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Referred Candidates */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Referred Candidates ({(detail.applications || []).length})</h3>
                </div>
                {(!detail.applications || detail.applications.length === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-6">No candidates referred yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase font-semibold">
                        <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Program</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Placement</th><th className="px-3 py-2 text-left">Visa</th><th className="px-3 py-2 text-left">Commission</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {detail.applications.slice(0, 30).map((app: any) => {
                          const comm = detailCommissions[app.id];
                          const commAmount = comm ? Number(comm.amount ?? comm.rate ?? 0) : (app.commissionRate != null ? Number(app.commissionRate) : null);
                          const commHistory: any[] = Array.isArray(comm?.history) ? comm.history : [];
                          return (
                          <React.Fragment key={app.id}>
                          <tr className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-slate-900">{app.firstName} {app.lastName}</td>
                            <td className="px-3 py-2 text-slate-600">{app.programType || ''}</td>
                            <td className="px-3 py-2"><span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-semibold">{app.status || ''}</span></td>
                            <td className="px-3 py-2 text-slate-600">{app.placementStatus || ''}</td>
                            <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${app.visaStatus === 'approved' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{app.visaStatus || ''}</span></td>
                            <td className="px-3 py-2">
                              {comm?.removedAt ? (
                                <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-semibold">Removed</span>
                              ) : commAmount != null ? (
                                <span className="text-slate-700 font-semibold">${commAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                              {comm?.payoutProcessedAt && <span className="ml-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold">Paid</span>}
                            </td>
                          </tr>
                          {commHistory.length > 0 && (
                            <tr>
                              <td colSpan={6} className="px-3 py-2 bg-slate-50/60">
                                <div className="space-y-1">
                                  {commHistory.slice().reverse().map((h: any, hi: number) => (
                                    <p key={hi} className="text-[11px] text-slate-500">
                                      <span className="font-semibold text-slate-700">{h.action}</span>
                                      {h.note ? <> — {h.note}</> : null}
                                      {' '}<span className="text-slate-400">({h.by?.name || h.by?.email || 'System'}{h.at ? ` · ${new Date(h.at).toLocaleDateString()}` : ''})</span>
                                    </p>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-100 p-5 flex items-center justify-between">
              <div className="flex gap-2">
                {detail.status === 'active' || detail.status === 'approved' ? (
                  <button onClick={() => { handleStatusChange(detail.id, 'inactive'); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-semibold transition cursor-pointer">Deactivate</button>
                ) : (
                  <button onClick={() => { handleStatusChange(detail.id, 'active'); setDetail(null); }} className="px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-xs font-semibold transition cursor-pointer">Activate</button>
                )}
                {canEdit && (
                  <button
                    onClick={() => { setDeleteTarget(detail); setDeleteConfirmText(''); }}
                    className="px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-1.5"
                    title="Permanently delete this organisation from the workspace"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete organisation
                  </button>
                )}
              </div>
              <button onClick={() => setDetail(null)} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Link User Modal */}
      {linkModal && (
        <Modal onClose={() => { setLinkModal(null); setLinkEmail(''); }} title="Link User to Referrer">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <Mail className="h-4 w-4 text-indigo-600" />
                Link User to Referrer
              </h2>
              <button aria-label="Close" title="Close" onClick={() => { setLinkModal(null); setLinkEmail(''); }} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-600">
                  Link a user account to <strong>{linkModal.name}</strong>. The user will log in with this email and access
                  the referrer dashboard.
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">User Email</label>
                <input type="email" required value={linkEmail} onChange={e => setLinkEmail(e.target.value)}
                  placeholder="user@example.com" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setLinkModal(null); setLinkEmail(''); }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">Cancel</button>
                <button
                  onClick={async () => {
                    if (!linkModal || !linkEmail.trim()) return;
                    setLinking(true);
                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const res = await wsFetch(`/api/admin/referrers/${linkModal.id}/link-user`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ email: linkEmail.trim().toLowerCase() }),
                      });
                      if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error(data.error || 'Failed to link user');
                      }
                      showToast(`User linked to ${linkModal.name}`, 'success');
                      setLinkModal(null);
                      setLinkEmail('');
                      fetchReferrers();
                    } catch (err: any) {
                      showToast(err.message, 'error');
                    } finally {
                      setLinking(false);
                    }
                  }}
                  disabled={linking || !linkEmail.trim()}
                  className="px-4 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition-all cursor-pointer flex items-center gap-1.5 shadow-lg shadow-brand-primary/20">
                  {linking ? 'Linking...' : 'Link User'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Referrer Modal */}
      {showCreateModal && (
        <Modal onClose={() => { setShowModal(false); resetCreateForm(); }} title="Add Referrer">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">Add Referrer</h3>
              <button aria-label="Close" title="Close" onClick={() => { setShowModal(false); resetCreateForm(); }} className="p-1 rounded hover:bg-slate-100 text-slate-500 cursor-pointer"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={handleCreateReferrer} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Referrer Name *</label>
                  <input required value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20" placeholder="e.g. Acme Referrals" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Type *</label>
                  <select value={createForm.type} onChange={e => setCreateForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="individual">Individual</option>
                    <option value="company">Company</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Country</label>
                  <input value={createForm.country} onChange={e => setCreateForm(f => ({ ...f, country: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. France" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Contact Name</label>
                  <input value={createForm.contactName} onChange={e => setCreateForm(f => ({ ...f, contactName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. Jean Dupont" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Contact Email</label>
                  <input type="email" value={createForm.contactEmail} onChange={e => setCreateForm(f => ({ ...f, contactEmail: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. jean@example.com" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Contact Phone</label>
                  <input value={createForm.contactPhone} onChange={e => setCreateForm(f => ({ ...f, contactPhone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. +33 6 12 34 56 78" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Website</label>
                  <input value={createForm.website} onChange={e => setCreateForm(f => ({ ...f, website: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. https://example.com" />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Programs Referring For</label>
                  <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-xl bg-white">
                    {programOptions.map((prog) => {
                      const selected = createForm.relatedPrograms.includes(prog.id);
                      return (
                        <button
                          key={prog.id}
                          type="button"
                          onClick={() => {
                            setCreateForm(f => ({
                              ...f,
                              relatedPrograms: selected
                                ? f.relatedPrograms.filter(id => id !== prog.id)
                                : [...f.relatedPrograms, prog.id]
                            }));
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition cursor-pointer ${
                            selected
                              ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                              : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {prog.name || prog.id}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); resetCreateForm(); }} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 cursor-pointer">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white btn-primary disabled:opacity-50 cursor-pointer">
                  {creating ? 'Creating...' : 'Create Referrer'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Delete Organisation — permanent removal, type-to-confirm */}
      {deleteTarget && (
        <Modal onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteConfirmText(''); } }} title="Delete referrer organisation">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-red-100 flex items-center justify-between">
              <h2 className="text-sm font-black text-red-700 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Delete referrer organisation
              </h2>
              <button aria-label="Close" title="Close" onClick={() => { if (!deleting) { setDeleteTarget(null); setDeleteConfirmText(''); } }} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-700">
                This permanently deletes <strong>{deleteTarget.name}</strong> from this workspace. <strong>This cannot be undone</strong> — archiving is the reversible alternative.
              </p>
              <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-4">
                <li>{(deleteTarget.linkedUsers || []).filter((u: any) => u.status !== 'removed').length} linked user(s) will be unlinked from this organisation. Their user accounts are <strong>not</strong> deleted — they keep their applicant accounts and any other roles.</li>
                {deleteTarget.inviteCode && (
                  <li>The referral code <strong className="font-mono">{deleteTarget.inviteCode}</strong> will stop working immediately. New signups using it will not be linked to any referrer.</li>
                )}
                <li>{deleteTarget.stats?.totalReferred ?? 0} referred application(s) will stay exactly as they are, showing this referrer as <strong>{deleteTarget.name} (organisation removed)</strong>.</li>
                <li>Commission records and their full history are preserved in Finance with the same removed marker.</li>
                <li>Any pending team invitations for this organisation will be cancelled.</li>
              </ul>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Type the organisation name to confirm: <span className="font-mono">{deleteTarget.name}</span>
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.name}
                  autoComplete="off"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                  disabled={deleting}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteReferrer}
                  disabled={deleting || deleteConfirmText.trim() !== String(deleteTarget.name || '').trim()}
                  className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center gap-1.5 shadow-lg shadow-red-600/20"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? 'Deleting...' : 'Delete permanently'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Invite Referrer Modal */}
      <AdminOrgInviteModal
        isOpen={showInviteModal}
        role="referrer"
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          notifyToast('success', 'Invitation sent successfully!');
          fetchReferrers();
        }}
        workspaceId={getActiveWorkspaceId() || ''}
        canEdit={canEdit}
      />
    </div>
  );
}
